import { UserProfile } from '../types';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

// Configuration depuis les variables d'environnement
const FEATUREBASE_APP_ID = import.meta.env.VITE_FEATUREBASE_APP_ID;
const FEATUREBASE_ORGANIZATION = import.meta.env.VITE_FEATUREBASE_ORGANIZATION;

// État d'initialisation
let isScriptLoaded = false;
let isInitialized = false;
let cachedFeaturebaseJwt: string | null = null;

export async function getFeaturebaseJwt(session: Session | null): Promise<string | null> {
  if (cachedFeaturebaseJwt) return cachedFeaturebaseJwt;

  // Vérifier qu'on a une session avec un token
  if (!session?.access_token) {
    console.warn('[Featurebase Service] No session token available for JWT generation');
    return null;
  }

  try {
    // Vérifier que le client Supabase a bien la session chargée
    const { data: currentSession, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !currentSession?.session?.access_token) {
      console.warn('[Featurebase Service] Supabase client has no active session', sessionError);
      return null;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const functionUrl = `${supabaseUrl}/functions/v1/featurebase-jwt`;
    
    console.log('[Featurebase Service] Calling featurebase-jwt Edge Function...', {
      hasAccessToken: !!currentSession.session.access_token,
      tokenLength: currentSession.session.access_token?.length,
      supabaseUrl,
      functionUrl
    });
    
    // Le client Supabase devrait automatiquement envoyer le token d'authentification
    // Ne pas passer de headers explicites pour éviter les conflits
    console.log('[Featurebase Service] Invoking function...');
    const { data, error } = await supabase.functions.invoke('featurebase-jwt');
    console.log('[Featurebase Service] Function response received', { 
      hasData: !!data, 
      hasError: !!error,
      errorCode: error?.code,
      errorMessage: error?.message
    });
    
    if (error) {
      console.error('[Featurebase Service] JWT function error:', error);
      console.error('[Featurebase Service] Full error object:', JSON.stringify(error, null, 2));
      
      // Vérifier si la fonction n'est pas déployée
      if (error.code === 'FUNCTION_NOT_FOUND' || (error as any).status === 404 || error.message?.includes('404')) {
        console.error('[Featurebase Service] ⚠️ Edge Function "featurebase-jwt" is not deployed!');
        console.error('[Featurebase Service] Deploy it with: supabase functions deploy featurebase-jwt');
        return null;
      }
      
      if (error.message?.includes('Invalid JWT') || error.message?.includes('401') || error.code === 401) {
        console.error('[Featurebase Service] Authentication failed - token may be expired or invalid');
        console.error('[Featurebase Service] Error details:', {
          code: error.code,
          message: error.message,
          status: (error as any).status
        });
        // Si l'authentification automatique échoue, essayer avec le token explicite
        console.log('[Featurebase Service] Retrying with explicit token...');
        const retryResponse = await supabase.functions.invoke('featurebase-jwt', {
          headers: {
            Authorization: `Bearer ${currentSession.session.access_token}`,
          },
        });
        if (retryResponse.error) {
          console.error('[Featurebase Service] Retry also failed:', retryResponse.error);
          return null;
        }
        const retryToken = retryResponse.data?.token;
        if (typeof retryToken === 'string' && retryToken) {
          console.log('[Featurebase Service] JWT token received on retry');
          cachedFeaturebaseJwt = retryToken;
          return retryToken;
        }
      }
      return null;
    }
    
    const token = data?.token;
    if (typeof token !== 'string' || !token) {
      console.warn('[Featurebase Service] Invalid token response:', data);
      return null;
    }

    console.log('[Featurebase Service] JWT token received successfully');
    cachedFeaturebaseJwt = token;
    return token;
  } catch (e) {
    console.error('[Featurebase Service] JWT invoke failed:', e);
    return null;
  }
}

/**
 * Charge le script SDK Featurebase de manière asynchrone
 */
export async function loadFeaturebaseScript(): Promise<void> {
  if (isScriptLoaded) {
    return Promise.resolve();
  }

  // Vérifier si le script est déjà présent
  if (document.getElementById('featurebase-sdk')) {
    isScriptLoaded = true;
    return Promise.resolve();
  }

  // Initialiser la fonction Featurebase si elle n'existe pas
  if (typeof window.Featurebase !== 'function') {
    window.Featurebase = function (...args: any[]) {
      (window.Featurebase.q = window.Featurebase.q || []).push(args);
    };
  }

  return new Promise((resolve, reject) => {
    // Charger le script
    const script = document.createElement('script');
    script.id = 'featurebase-sdk';
    script.src = 'https://do.featurebase.app/js/sdk.js';
    script.async = true;
    
    script.onload = () => {
      isScriptLoaded = true;
      resolve();
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load Featurebase SDK script'));
    };
    
    // Insérer le script avant le premier script existant
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });
}

/**
 * Identifie l'utilisateur pour l'auto-authentication (portail et widgets)
 */
async function identify(userProfile: UserProfile, session: Session | null): Promise<void> {
  if (!FEATUREBASE_ORGANIZATION) {
    console.warn('Featurebase organization is not configured, skipping identify');
    return;
  }

  if (!window.Featurebase) {
    throw new Error('Featurebase SDK is not loaded');
  }

  const featurebaseJwt = await getFeaturebaseJwt(session);
  if (!featurebaseJwt) {
    console.warn('[Featurebase Service] JWT missing, skipping identify (secure installation required)');
    return;
  }

  return new Promise((resolve, reject) => {
    const identifyParams: any = {
      organization: FEATUREBASE_ORGANIZATION,
      featurebaseJwt,
      // Non-sensitive attrs can be passed outside the JWT if needed:
      // locale: 'fr',
    };

    // Callback pour gérer le résultat
    const callback = (err?: Error | null) => {
      if (err) {
        console.error('Featurebase identify error:', err);
        reject(err);
      } else {
        console.log('Featurebase identify successful');
        resolve();
      }
    };

    window.Featurebase!('identify', identifyParams, callback);
  });
}

/**
 * Boot le Messenger widget (global)
 */
async function bootMessenger(userProfile: UserProfile, session: Session | null): Promise<void> {
  if (!FEATUREBASE_APP_ID) {
    console.warn('Featurebase app ID is not configured, skipping messenger boot');
    return;
  }

  if (!window.Featurebase) {
    throw new Error('Featurebase SDK is not loaded');
  }

  const featurebaseJwt = await getFeaturebaseJwt(session);
  if (!featurebaseJwt) {
    console.warn('[Featurebase Service] JWT missing, skipping messenger boot (secure installation required)');
    return;
  }

  const bootParams: any = {
    appId: FEATUREBASE_APP_ID,
    featurebaseJwt,
    theme: 'light', // Peut être configuré selon le thème de l'app
    language: 'fr', // Français par défaut
  };

  window.Featurebase!('boot', bootParams);
}

/**
 * Initialisation complète de Featurebase
 */
export async function initFeaturebase(userProfile: UserProfile, session: Session | null): Promise<void> {
  console.log('[Featurebase Service] initFeaturebase called', {
    hasAppId: !!FEATUREBASE_APP_ID,
    hasOrganization: !!FEATUREBASE_ORGANIZATION,
    appId: FEATUREBASE_APP_ID,
    organization: FEATUREBASE_ORGANIZATION,
    userId: userProfile?.id
  });

  if (isInitialized) {
    console.log('[Featurebase Service] Already initialized, skipping');
    return;
  }

  // Vérifier la configuration
  if (!FEATUREBASE_APP_ID || !FEATUREBASE_ORGANIZATION) {
    console.warn('[Featurebase Service] Not fully configured, skipping initialization', {
      hasAppId: !!FEATUREBASE_APP_ID,
      hasOrganization: !!FEATUREBASE_ORGANIZATION
    });
    return;
  }

  try {
    // 1. Charger le script SDK
    await loadFeaturebaseScript();

    // 2. Attendre que le script soit complètement chargé
    // Le script peut mettre un peu de temps à initialiser la fonction Featurebase
    let retries = 0;
    while (typeof window.Featurebase !== 'function' && retries < 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }

    if (typeof window.Featurebase !== 'function') {
      throw new Error('Featurebase SDK failed to initialize');
    }

    // 3. Identifier l'utilisateur (auto-auth pour portail/widgets)
    await identify(userProfile, session);

    // 4. Boot le Messenger widget
    await bootMessenger(userProfile, session);

    isInitialized = true;
    console.log('Featurebase initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Featurebase:', error);
    throw error;
  }
}

/**
 * Nettoyage de Featurebase (lors de la déconnexion)
 */
export function cleanupFeaturebase(): void {
  // Note: Featurebase ne fournit pas de méthode de cleanup explicite
  // Le widget reste en mémoire mais ne sera plus utilisé
  // On peut réinitialiser les flags pour permettre une nouvelle initialisation
  isInitialized = false;
  cachedFeaturebaseJwt = null;
  // Note: On ne supprime pas le script car il peut être réutilisé
}

/**
 * Vérifie si Featurebase est initialisé
 */
export function isFeaturebaseInitialized(): boolean {
  return isInitialized && typeof window.Featurebase === 'function';
}
